import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import * as Util from '../../../common/utilities';
import { withSession } from '../../../i18n/containers/AppInit.js';
import { NoDataScreen } from '../../../ecs/components';

import {
  ArchiveExperimentModal,
  DeleteExperimentModal,
  EndExperimentModal,
  ExperimentList
} from '../';

import './ExperimentOverview.scss';
import {
  Card,
  MediaObject,
  Spinner,
  Toast,
  ToastContainer
} from '@salesforce/design-system-react';

import { HeaderDropdown } from '../../../common/components/';

class ExperimentOverview extends Component {
  constructor(props) {
    super(props);
    this.state = {
      archiveFilter: 'unarchived'
    };
  }

  componentDidMount() {
    this.props.actions.searchExperiments(
      {},
      { page: 1 },
      { context: 'overview', mode: 'reset' }
    );
    this.props.actions.getArchivedExperiments();
  }

  componentDidUpdate(prevProps) {
    if (
      Util.propChanged(
        prevProps,
        this.props,
        'experiments.data.search.overview.paging.page'
      )
    ) {
      this.isPaging = false;
    }

    if (Util.propChanged(prevProps, this.props, 'experiments.data.lastSave')) {
      if (this.props.experiments.data.lastSave) {
        const { lastSave } = this.props.experiments.data;
        if (lastSave.error) {
          this.props.actions.openToast('apiError', {
            ...lastSave.error
          });
        } else if (lastSave.mode === 'create' || lastSave.mode === 'edit') {
          this.props.actions.openToast('experiment', {
            name: lastSave.experiments.name,
            mode: lastSave.mode
          });
          this.props.actions.searchExperiments(
            {},
            { page: 1 },
            { context: 'overview', mode: 'reset' }
          );
        } else if (lastSave.mode === 'archive') {
          this.props.actions.openToast('experiment', {
            name: lastSave.experiments.name,
            mode: lastSave.mode
          });
          this.props.actions.getArchivedExperiments();
        } else {
          this.props.actions.openToast('experiment', {
            name: lastSave.experiments.name,
            mode: lastSave.mode
          });
        }
      }
    }
  }

  hasMore() {
    return this.props.experiments?.data?.search?.overview?.paging?.more;
  }

  isPaging = false;

  handleLoadMore() {
    if (!this.isPaging) {
      this.isPaging = true;
      const currentPage =
        this.props.experiments?.data?.search?.overview?.paging?.page || 0;
      this.props.actions.searchExperiments(
        this.props.experiments.browsedFilter,
        { page: currentPage + 1 },
        { context: 'overview' }
      );
    }
  }

  header(count = 0) {
    const { formatMessage } = this.props.intl;
    const i18nKey = 'components.experiment';
    return (
      <div className="experiment-header">
        <HeaderDropdown
          options={[
            {
              label: formatMessage({ id: `${i18nKey}.header` }),
              value: 'unarchived'
            },
            {
              label: formatMessage({ id: `${i18nKey}.archived` }),
              value: 'archived'
            }
          ]}
          value={this.state.archiveFilter}
          onSelect={option => this.setState({ archiveFilter: option.value })}
        />
        <FormattedMessage id="components.experiment.items" values={{ count }} />
      </div>
    );
  }

  handleListAction(session) {
    return (action, experiment, disabled) => {
      if (this.props.flags?.isReadonly) {
        return null;
      }
      switch (action) {
        case 'edit':
          const loaded = JSON.parse(JSON.stringify(experiment));
          this.props.actions.openModal('experimentWizard', {
            mode: 'edit',
            experiment,
            context: 'overview',
            loadedExperiment: loaded,
            saveDisabled: true,
            finishDisabled: experiment.content_test_assets?.length < 2
          });
          break;
        case 'delete':
          this.props.actions.openModal('deleteExperiment', experiment, {
            cToken: session.experimentCsrfToken
          });
          break;
        case 'archive':
          this.props.actions.openModal('archiveExperiment', experiment, {
            cToken: session.experimentCsrfToken
          });
          break;
        case 'end':
          this.props.actions.openModal('endExperiment', experiment, {
            cToken: session.experimentCsrfToken
          });
          break;
        case 'publish':
          this.props.actions.publishExperiment(experiment, 'overview', {
            cToken: session.experimentCsrfToken
          });
          break;
        default:
          break;
      }
    };
  }

  filterExperiments(experiments) {
    /* dont sort by name
    const unfilteredExperiments = experiments.sort(
      StringUtils.sortByField('name', this.props.intl.locale)
    );

    const filtered = [];
    unfilteredExperiments.forEach(unfilteredExperiment => {
      filtered.push(unfilteredExperiment);
    });
    return filtered;
    */
    return experiments;
  }

  drawSpinner(isLoading) {
    return isLoading ? (
      <div className="pi-common-spinner">
        <Spinner variant="brand" />
      </div>
    ) : null;
  }

  renderNoData(flags) {
    return (
      <Card
        className="experiment-card"
        header={<MediaObject body={this.header(0)} />}
      >
        <NoDataScreen
          screen="experiments"
          helpLink="https://help.salesforce.com/articleView?id=sf.mc_pb_einstein_content_selection_test_content_before_sending.htm&type=5"
        />
      </Card>
    );
  }

  demoExperiments(filtered) {
    return filtered.map(item => ({
      ...item,
      selections:
        item.status !== 'DRAFT'
          ? Math.ceil(Math.random() * 5499 + 1999)
          : item.selections,
      clicks:
        item.status !== 'DRAFT'
          ? Math.ceil(Math.random() * 99 + 19)
          : item.clicks
    }));
  }

  renderTable(filtered, experiments, session) {
    const filteredExperiments = this.props.flags.demo
      ? this.demoExperiments(filtered)
      : filtered;
    return (
      <Card
        className="experiment-card"
        header={<MediaObject body={this.header(filtered?.length)} />}
      >
        <div className="experiment-list-div">
          <ExperimentList
            experiments={filteredExperiments}
            flags={this.props.flags}
            unfilteredExperiments={experiments}
            actions={this.props.actions}
            onListAction={this.handleListAction(session)}
            hasMore={this.hasMore()}
            onLoadMore={() => this.handleLoadMore()}
          />
        </div>
      </Card>
    );
  }

  experimentToast() {
    const { formatMessage } = this.props.intl;
    if (
      this.props.toast.experiment &&
      this.props.toast.experiment.mode !== 'winner'
    ) {
      const heading = formatMessage(
        {
          id: `components.experiment.toast.${this.props.toast.experiment.mode}`
        },
        { experiment: this.props.toast.experiment.name }
      );
      return (
        <Toast
          labels={{ heading }}
          variant="success"
          duration={10000}
          onRequestClose={() => this.props.actions.closeToast('experiment')}
        />
      );
    }

    return null;
  }

  toast() {
    return <React.Fragment>{this.experimentToast()}</React.Fragment>;
  }

  render() {
    const { isLoading } = this.props.experiments?.isLoading || false;
    const { flags } = this.props;
    const unarchived =
      this.props.experiments?.data?.search?.overview?.experiments || [];
    const archived = this.props.experiments?.data?.archived?.experiments || [];
    const experiments =
      this.state.archiveFilter === 'unarchived' ? unarchived : archived;
    const canRender = !!experiments;
    const filtered = this.filterExperiments(experiments);
    const context = 'overview';
    return withSession(session => {
      return (
        <React.Fragment>
          {this.drawSpinner(isLoading)}
          {canRender &&
          !experiments.length &&
          this.state.archiveFilter === 'unarchived'
            ? this.renderNoData(flags)
            : this.renderTable(filtered, experiments, session)}
          <ArchiveExperimentModal
            actions={this.props.actions}
            context={context}
            modals={this.props.modals}
            flags={flags}
            session={session}
          />
          <DeleteExperimentModal
            actions={this.props.actions}
            context={context}
            modals={this.props.modals}
            flags={flags}
            session={session}
          />
          <EndExperimentModal
            actions={this.props.actions}
            context={context}
            modals={this.props.modals}
            flags={flags}
            session={session}
          />
          <ToastContainer>{this.toast()}</ToastContainer>
        </React.Fragment>
      );
    });
  }
}

ExperimentOverview.propTypes = {
  experiments: PropTypes.object.isRequired,
  flags: PropTypes.object.isRequired,
  toast: PropTypes.object,
  actions: PropTypes.object,
  modals: PropTypes.object.isRequired,
  location: PropTypes.object
};

export default injectIntl(ExperimentOverview);
